// Image cache implementation
const imageCache = new Map<string, string>();

/**
 * Preloads an image and stores it in the cache
 * @param url The URL of the image to preload
 * @returns Promise that resolves when image is loaded
 */
export const preloadImage = (url: string): Promise<void> => {
  return new Promise((resolve) => {
    if (imageCache.has(url)) {
      resolve();
      return;
    }

    const img = new Image();
    img.src = url;
    img.onload = () => {
      imageCache.set(url, url);
      resolve();
    };
    img.onerror = () => {
      // Even on error, resolve to not block other preloads
      console.warn('[ImageCache] Failed to preload image:', url);
      resolve();
    };
  });
};

/**
 * Preloads multiple images in parallel
 * @param urls Array of URLs to preload
 * @returns Promise that resolves when all images are loaded
 */
export const preloadImages = async (urls: string[]): Promise<void> => {
  const validUrls = urls.filter(url => url && url.trim() !== '');
  await Promise.all(validUrls.map(url => preloadImage(url)));
};

/**
 * Retrieves an image from the cache, falling back to the original URL if not cached
 * @param url The URL of the image to retrieve
 * @returns The cached image URL or the original URL if not cached
 */
export const getCachedImage = (url: string): string => {
  return imageCache.get(url) || url;
};

/**
 * Checks if an image is already cached
 * @param url The URL to check
 * @returns true if cached, false otherwise
 */
export const isImageCached = (url: string): boolean => {
  return imageCache.has(url);
};

/**
 * Clears the entire image cache
 */
export const clearImageCache = (): void => {
  imageCache.clear();
};